import { proxy, useSnapshot } from 'valtio';

import { getSanitizedErrorFromError, toError } from '@canvas/error';
import {
  type CapabilityTier,
  captureException,
  type CustomerImpact,
} from '@canvas/sentry-react';

import { identifyError } from './identify-error';
import { sendErrorDetailsToSentry } from './send-error-details-to-sentry';
import { type CanvasNonFatalError, type IdentifiedError } from './types';

export type ErrorState = {
  error?: IdentifiedError;
  captureLocation?: string;
};

type Listener = (error: Error, sentryErrorId?: string) => void;
type NonFatalListener = (
  error: Error,
  errorId: CanvasNonFatalError,
  sentryErrorId?: string
) => void;

export { CanvasErrorType } from './types';

class GlobalErrorManager {
  state = proxy<ErrorState>({
    error: undefined,
    captureLocation: undefined,
  });
  fatalListeners: Listener[] = [];
  nonFatalListeners: NonFatalListener[] = [];

  /**
   * a React hook that returns the current error if it exists
   */
  useFatalError() {
    // disabling this lint rule which incorrectly believes this class to be a React component
    /* eslint-disable-next-line react-hooks/rules-of-hooks */
    return useSnapshot(this.state).error;
  }

  /**
   * returns a boolean if an unrecoverable error exists
   */
  hasUnrecoverableError() {
    const { error } = this.state;
    if (error && !error.isRecoverable) {
      return true;
    }
    return false;
  }

  /**
   * a React hook that returns the current error if it exists and is unrecoverable
   */
  useUnrecoverableError() {
    const error = this.useFatalError();
    if (error && !error.isRecoverable) {
      return error;
    }
    return undefined;
  }

  /**
   * sets the current global, fatal error
   *
   * please avoid importing this ad-hoc into files to handle
   * errors directly prefer throwing errors and have globalErrorManager
   * catch them at the top of the app if possible
   *
   * @param maybeError an error or anything that will be turned into an error
   * @param captureLocation a string describing where captureFatalError() is being called
   */
  captureFatalError(maybeError: Error | unknown, captureLocation: string) {
    if (process.env.NODE_ENV === 'development') {
      console.error(maybeError);
    }

    const error = toError(maybeError);
    error.message = getSanitizedErrorFromError(error);
    const identifiedError = identifyError(error);

    identifiedError.sentryErrorId = sendErrorDetailsToSentry(
      identifiedError,
      captureLocation
    );

    // if we already have an unrecoverable error and we receive another, ignore it
    if (this.hasUnrecoverableError() && !identifiedError.isRecoverable) {
      return;
    }

    this.state.error = identifiedError;
    this.state.captureLocation = captureLocation;
    this.fatalListeners.forEach((listener) =>
      listener(identifiedError.error, identifiedError.sentryErrorId)
    );
  }

  /**
   * notifies all listeners of the non-fatal error passed in
   *
   * it is okay to import globalErrorManager directly when calling captureNonFatalError
   *
   * @param errorId an identifier from the CanvasNonFatalError enum
   * @param maybeError an error or anything that will be turned into an error
   * @param metadata metadata to send to sentry
   */
  captureNonFatalError(
    errorId: CanvasNonFatalError,
    maybeError: Error | unknown,
    metadata: {
      captureLocation: string;
      capabilityTier?: CapabilityTier;
      customerImpact?: CustomerImpact;
    }
  ) {
    const error = toError(maybeError);
    error.message = getSanitizedErrorFromError(error);

    const sentryErrorID = captureException(error, {
      tags: {
        captureLocation: metadata.captureLocation,
        capabilityTier: metadata.capabilityTier,
        customerImpact: metadata.customerImpact,
      },
    });

    this.nonFatalListeners.forEach((listener) => listener(error, errorId, sentryErrorID));
  }

  /**
   * clears the current error
   */
  clearError() {
    this.state.error = undefined;
    this.state.captureLocation = undefined;
  }

  /**
   * Sets up a listener callback that is invoked whenever a fatal error is caught
   *
   * @param listener callback that will be invoked whenever a fatal error is caught
   */
  onFatalError(listener: Listener) {
    this.fatalListeners.push(listener);
  }

  /**
   * Sets up a listener callback that is invoked whenever a non-fatal error is caught
   *
   * @param listener callback that will be invoked whenever a non-fatal error is caught
   */
  onNonFatalError(listener: NonFatalListener) {
    this.nonFatalListeners.push(listener);
  }

  /**
   * clears all listeners
   */
  clearOnErrorListeners() {
    this.fatalListeners = [];
    this.nonFatalListeners = [];
  }
}

/**
 * this instantiates and exports the GlobalErrorManager instance
 * This is used primarily at the top of the app, set up to catch
 * any errors that may be thrown by the rest of the app
 */
export const globalErrorManager = new GlobalErrorManager();
